import { useState, useEffect, useCallback } from 'react';

function storageKey(projectId: string): string {
  return `pm:listViewCollapsedStages:${projectId}`;
}

function loadCollapsed(projectId: string): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey(projectId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return new Set();
  }
}

function saveCollapsed(projectId: string, collapsed: Set<string>): void {
  try {
    localStorage.setItem(storageKey(projectId), JSON.stringify(Array.from(collapsed)));
  } catch {
    /* ignore */
  }
}

export function useListViewStageCollapse(projectId: string) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsed(projectId));

  useEffect(() => {
    setCollapsed(loadCollapsed(projectId));
  }, [projectId]);

  const isCollapsed = useCallback((stageId: string) => collapsed.has(stageId), [collapsed]);

  const toggleCollapsed = useCallback(
    (stageId: string) => {
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(stageId)) {
          next.delete(stageId);
        } else {
          next.add(stageId);
        }
        saveCollapsed(projectId, next);
        return next;
      });
    },
    [projectId],
  );

  return { isCollapsed, toggleCollapsed };
}
